import {mount} from 'svelte';

import I18n from '@js/modules/translations';
import {onPageReady} from '@js/modules/utils';

import Notification from '@js/components/layouts/notification';

import AnalyticsService from '@js/modules/analyticsService';
import StorageService from '@js/modules/storage';

const BiggerPicture = () => import('bigger-picture');

const RideShow = () => import('@js/components/ride/RideShow.svelte');

const RideElevationGraph = () => import('@js/components/ride/shared/RideElevationGraph.svelte');

const ShareRideButton = () => import('@js/components/ride/share/ShareRideButton.svelte');

const TimeToStartButton = () => import('@js/components/maps/timeToStart/TimeToStartButton.svelte');
const HeartRateGraph = () => import('@js/components/maps/HeartRateGraph.svelte');


class RideManager {
    _ride = null;

    _biggerPicture = null;

    _$rideData = document.getElementById('ride-data');

    _$map = document.getElementById('ride-map-component');
    _$mapElevation = document.getElementById('ride-map-elevation-component');
    _$shareButton = document.getElementById('share-component');
    _$timeToStartButton = document.getElementById('ride-time-to-start-component');
    _$heartRate = document.getElementById('ride-heart-rate-component');

    _$images = document.querySelectorAll('#ride-carrousel a');


    /* Public methods
    ******************** */
    constructor() {
        this._ride = JSON.parse(this._$rideData.innerText)?.ride;
    }

    initialize = () => {
        this._saveVisitedRide();

        this._mountComponents();

        this._manageImages();

        this._onExportClick();

        this._onOpenMapClick();
    };

    /* Private methods
    ******************** */
    _saveVisitedRide = () => {
        StorageService.saveLocalArray('rides-visited', [this._ride.id], true, true);
    };

    _mountComponents = () => {
        RideShow()
            .then(({default: RideShowComponent}) => {
                mount(RideShowComponent, {
                    target: this._$map,
                    props: {ride: this._ride}
                });
            });

        if (this._$mapElevation) {
            onPageReady(() => {
                RideElevationGraph()
                    .then(({default: RideElevationGraphComponent}) => {
                        mount(RideElevationGraphComponent, {
                            target: this._$mapElevation,
                            props: {
                                rideId: this._ride.id,
                                rideShareUrl: this._ride.shareUrl
                            }
                        });
                    });
            });
        }

        if (this._$shareButton) {
            onPageReady(() => {
                ShareRideButton()
                    .then(({default: ShareRideButtonComponent}) => {
                        mount(ShareRideButtonComponent, {
                            target: this._$shareButton,
                            props: {
                                rideId: this._ride.id,
                                rideName: this._ride.name,
                                rideUrl: window.location.origin + this._ride.link,
                                rideVisibility: this._ride.visibility,
                                rideSharedUrl: this._ride.sharedUrl
                            }
                        });
                    });
            });
        }

        if (this._$timeToStartButton) {
            onPageReady(() => {
                TimeToStartButton()
                    .then(({default: TimeToStartButtonComponent}) => {
                        mount(TimeToStartButtonComponent, {
                            target: this._$timeToStartButton,
                            props: {rideId: this._ride.id}
                        });
                    });
            });
        }

        if (this._$heartRate) {
            onPageReady(() => {
                HeartRateGraph()
                    .then(({default: HeartRateGraphComponent}) => {
                        mount(HeartRateGraphComponent, {
                            target: this._$heartRate,
                            props: {ride: this._ride}
                        });
                    });
            });
        }
    };

    _openImage = (event) => {
        event.preventDefault();

        this._biggerPicture.open({
            items: this._$images,
            el: event.currentTarget,
            scale: 0.85,
            maxZoom: 3
        });
    };

    _manageImages = () => {
        onPageReady(() => {
            BiggerPicture()
                .then(({default: BiggerPictureComponent}) => {
                    import('bigger-picture/css');

                    this._biggerPicture = BiggerPictureComponent({
                        target: document.body
                    });

                    this._$images.forEach(($image) => $image.addEventListener('click', this._openImage));
                });
        });
    };

    _onExportClick = () => {
        const $exportButton = document.getElementById('export-gpx');
        if (!$exportButton) {
            return;
        }

        $exportButton.onclick = () => {
            AnalyticsService.trackRouteExported(this._ride.id, this._ride.name);

            Notification.info(I18n.t('js.ride.map.export_gpx_progress'));
        };
    };

    _onOpenMapClick = () => {
        const $openMap = document.getElementById('open-map');
        if (!$openMap) {
            return;
        }

        $openMap.addEventListener('click', (event) => {
            event.preventDefault();

            const lat = $openMap.dataset.lat;
            const lng = $openMap.dataset.lng;
            const label = encodeURIComponent($openMap.dataset.label);
            const userAgent = navigator.userAgent || navigator.vendor;

            if (/iPad|iPhone|iPod/.test(userAgent)) {
                window.location.href = `https://maps.apple.com/?ll=${lat},${lng}&q=${label}`;
            } else if (/android/i.test(userAgent)) {
                window.location.href = `geo:${lat},${lng}?q=${label}`;
            } else {
                window.location.href = `https://www.google.com/maps?q=${lat},${lng}(${label})`;
            }
        });
    };
}

export default new RideManager();
